import React, { useState } from 'react';
import { ExternalLink, ShoppingCart, Calendar as CalendarIcon, Bell, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Merchandise = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const comingSoonProducts = [
    {
      id: 1,
      name: 'A Holistic Selfcare Journey - The Journal',
      description: 'Your personal companion for self-reflection, growth, and holistic wellness. Track your journey to becoming your best self.',
      image: 'https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/bplsqfeo_f8b99783-31b0-4e81-92b2-d7fd301390ad.jpeg',
      link: 'https://a.co/d/02KIs6S9',
      badge: 'Now Available',
    },
    {
      id: 2,
      name: 'A Holistic Selfcare Journey - The Workbook',
      description: 'A comprehensive workbook filled with exercises, prompts, and tools to guide you through your selfcare journey.',
      image: 'https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/tpl5bkxk_8b6f5c49-e98c-4e2c-b775-cf9e940a4c29.jpeg',
      link: 'https://a.co/d/05EQ7uCC',
      badge: 'Now Available',
    },
  ];

  const featuredProducts = [
    {
      id: 1,
      name: 'Hearth Calendar Display',
      description: 'Beautiful calendar display to keep your family organized in style!',
      image: 'https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/uajrrk4h_IMG_0342.jpeg',
      link: 'https://hearthdisplay.com/JATNNAABREU',
      featured: true,
    },
  ];

  const handleNotifyMe = async (productName) => {
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    setSelectedProduct(productName);

    try {
      await axios.post(`${API}/notify-me`, {
        email: email,
        product: productName,
      });

      toast({
        title: 'You\'re on the list! 🎉',
        description: `We'll notify you at ${email} when "${productName}" launches!`,
      });
      setEmail('');
    } catch (error) {
      toast({
        title: 'Oops!',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setSelectedProduct('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Shop Our Merch
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Represent the movement! Show your support and pride with our exclusive 
            Dos Bold Latinas merchandise.
          </p>
        </div>
      </section>

      {/* Merchandise Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Now Available Products */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                Now <span style={{ color: '#b76e79' }}>Available</span> on Amazon!
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our self-care journal and workbook are now available for purchase on Amazon. 
                Start your holistic self-care journey today!
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {comingSoonProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="border-none shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-96 bg-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute top-4 right-4">
                      <span 
                        className="px-4 py-2 rounded-full text-white font-semibold text-sm"
                        style={{ backgroundColor: '#b76e79' }}
                      >
                        {product.badge}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold mb-3">{product.name}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {product.description}
                    </p>
                    
                    <a 
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button 
                        className="w-full transition-all duration-300 hover:scale-105"
                        style={{ backgroundColor: '#b76e79' }}
                        size="lg"
                      >
                        <ShoppingCart className="mr-2" size={20} />
                        Buy on Amazon
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Featured Product - Calendar */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8">
              Featured <span style={{ color: '#b76e79' }}>Product</span>
            </h2>
            <Card className="border-none shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-96 bg-gray-100">
                  <img
                    src={featuredProducts[0].image}
                    alt={featuredProducts[0].name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <CardContent className="p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <CalendarIcon size={32} style={{ color: '#b76e79' }} />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">{featuredProducts[0].name}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    {featuredProducts[0].description}
                  </p>
                  <Button 
                    size="lg"
                    className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105 w-full md:w-auto"
                    style={{ backgroundColor: '#b76e79' }}
                    onClick={() => window.open(featuredProducts[0].link, '_blank')}
                  >
                    <ShoppingCart className="mr-2" size={20} />
                    Buy Calendar Display
                    <ExternalLink className="ml-2" size={18} />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center border-t-4 border-none shadow-lg" style={{ borderTopColor: '#b76e79' }}>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">Quality Products</h3>
                <p className="text-gray-600">Premium materials & designs you'll love</p>
              </CardContent>
            </Card>
            <Card className="text-center border-t-4 border-none shadow-lg" style={{ borderTopColor: '#b76e79' }}>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">Show Your Support</h3>
                <p className="text-gray-600">Represent the Dos Bold Latinas community</p>
              </CardContent>
            </Card>
            <Card className="text-center border-t-4 border-none shadow-lg" style={{ borderTopColor: '#b76e79' }}>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">Support Our Mission</h3>
                <p className="text-gray-600">Proceeds fund our podcast & initiatives</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Merchandise;